import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

import { errorText } from '../components/custom_creative_session/DevelopmentStage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * The Research Exploration step's behaviour — the state machine, once.
 *
 * RESEARCH-EXPLORATION-REDESIGN, 2026-09-10. v1's `components/ExplorationPhase`
 * holds its own copy of these seven calls. That copy is left alone (v1 is
 * retired in wave 5 and restyling it would put v2 classes on a surface that
 * cannot see them), but nothing NEW gets a second implementation: v2's screen
 * reads this, and any later port points here too.
 *
 * WHAT THIS HOOK REFUSES TO INVENT
 * ================================
 * Founder ruling 2026-09-10: "no seeded or mock data anywhere, and nothing shown
 * on screen may be a placeholder standing in for real information."
 *
 * So: the sources come from the server's own catalogue, never a list in the
 * frontend. `readiness` is computed from fields on the document and each part of
 * it names where it came from. An objective the proposer did not write is
 * `null`, and the screen says it is unset — it is not filled in with a sentence
 * assembled from the offer.
 *
 * THE DEFECT THIS REPLACES. v1's screen offered four sources — `google_trends`,
 * `reddit`, `competitor_ads`, `google_search` — that the service has had no
 * adapter for since the Ghost Recon v2 removal. Measured live: 44 of 81
 * exploration documents carry those names, and `exp_f5bea55e5e7c` is LOCKED with
 * 3 rounds x 44 queries x 0 results — a research brief written from no research.
 */

//: The six states the service can be in. `BRIEF_FAILED` is included because v1's
//: screen renders five of them and draws a bare header on the sixth, while the
//: service documents `lock` as retriable from exactly there.
export const STATES = {
  PROPOSED: 'TARGETS_PROPOSED',
  CONFIRMED: 'TARGETS_CONFIRMED',
  FINDINGS: 'FINDINGS_READY',
  REVIEW: 'BRIEF_IN_REVIEW',
  FAILED: 'BRIEF_FAILED',
  LOCKED: 'LOCKED',
};

/** A PAGE_SCRAPE target has to be a URL — the adapter cannot take a topic. */
const looksLikeUrl = (s) => /^https?:\/\/\S+$/i.test((s || '').trim());

export function useExploration({ sessionId, onProceed }) {
  const [doc, setDoc] = useState(null);
  const [loadState, setLoadState] = useState('idle');   // idle|loading|ok|failed
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState(null);               // the action's own label
  const [actionError, setActionError] = useState(null);

  // The operator's unsaved edits to the target list, before Confirm.
  const [draftTargets, setDraftTargets] = useState([]);
  const [draftSources, setDraftSources] = useState([]);

  const call = useCallback(async (label, fn) => {
    setBusy(label);
    setActionError(null);
    try {
      const res = await fn();
      if (res?.data) setDoc(res.data);
      return res?.data ?? null;
    } catch (e) {
      // Named, never swallowed. The service's 409s carry the reason it refused.
      setActionError(errorText(e));
      return null;
    } finally {
      setBusy(null);
    }
  }, []);

  // Start is idempotent — it returns the in-progress document when one exists.
  useEffect(() => {
    if (!sessionId) return;
    let live = true;
    setLoadState('loading');
    axios.post(`${API}/creative/sessions/${sessionId}/exploration/start`)
      .then((res) => {
        if (!live) return;
        setDoc(res.data);
        setLoadState('ok');
        setLoadError(null);
      })
      .catch((e) => {
        if (!live) return;
        // A failed start is a failure, not an empty exploration.
        setLoadState('failed');
        setLoadError(errorText(e));
      });
    return () => { live = false; };
  }, [sessionId]);

  // Seed the editable list from whatever the engine last proposed. Only while
  // targets are still being reviewed: once confirmed, the stored list is the
  // truth and an editable copy would drift from it silently.
  useEffect(() => {
    if (doc?.state !== STATES.PROPOSED) return;
    setDraftTargets((doc.proposed_targets || []).map((t) => ({
      target_id: t.target_id || null,
      label: t.label || '',
      source_kind: (t.source_kind || 'SEARCH').toUpperCase(),
      rationale: t.rationale || '',
    })));
    if (Array.isArray(doc.selected_sources) && doc.selected_sources.length) {
      setDraftSources(doc.selected_sources);
    }
  }, [doc?.state, doc?.proposed_targets, doc?.selected_sources]);

  // Auto-advance once the brief is locked — the wizard takes over from here.
  useEffect(() => {
    if (doc?.state !== STATES.LOCKED) return undefined;
    const t = setTimeout(() => onProceed && onProceed(), 600);
    return () => clearTimeout(t);
  }, [doc?.state, onProceed]);

  // ── the catalogue: what the service can actually query ────────────────────
  const catalogue = doc?.catalogue || null;
  const kinds = useMemo(() => catalogue?.kinds || [], [catalogue]);
  const registered = catalogue?.registered_sources || [];

  // ── target editing (local until Confirm) ─────────────────────────────────
  const setTarget = (i, patch) =>
    setDraftTargets((ts) => ts.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));
  const removeTarget = (i) =>
    setDraftTargets((ts) => ts.filter((_, idx) => idx !== i));
  const addTarget = (kind) =>
    setDraftTargets((ts) => [...ts, { target_id: null, label: '',
                                      source_kind: kind || 'SEARCH', rationale: '' }]);
  const toggleSource = (name) =>
    setDraftSources((s) => (s.includes(name) ? s.filter((x) => x !== name) : [...s, name]));

  // ── actions ──────────────────────────────────────────────────────────────
  const saveObjective = (text) => call('Saving the objective', () =>
    axios.patch(`${API}/creative/sessions/${sessionId}/exploration/objective`,
                { objective: text }));

  const confirmTargets = () => call('Confirming the plan', () =>
    axios.post(`${API}/creative/sessions/${sessionId}/exploration/targets/confirm`, {
      confirmed_targets: draftTargets
        .filter((t) => (t.label || '').trim())
        .map((t) => ({ target_id: t.target_id, label: t.label.trim(),
                       source_kind: t.source_kind, rationale: t.rationale })),
      sources: draftSources,
    }));

  const runFetch = () => call('Researching', () =>
    axios.post(`${API}/creative/sessions/${sessionId}/exploration/fetch`));

  /* ONE source, ONE corrected query — EMPTY-SOURCE-LOUD-AND-RETRYABLE.
     Not `runFetch`: a round re-sends every confirmed target and spends one of
     three round slots, so fixing one query there would re-pay for the rest. */
  const retrySource = (sourceKind, label) =>
    call('Retrying', () => axios.post(
      `${API}/creative/sessions/${sessionId}/exploration/retry-source`,
      { source_kind: sourceKind, label }));

  const sendMessage = (text) => {
    const t = (text || '').trim();
    if (!t) return Promise.resolve(null);
    return call('Thinking', () =>
      axios.post(`${API}/creative/sessions/${sessionId}/exploration/message`, { text: t }));
  };

  const synthesise = () => call('Writing the brief', () =>
    axios.post(`${API}/creative/sessions/${sessionId}/exploration/lock`));

  const approveBrief = () => call('Locking the brief', () =>
    axios.post(`${API}/creative/sessions/${sessionId}/exploration/brief/review`,
               { decision: 'approve' }));

  const reviseBrief = (feedback) => call('Revising the brief', () =>
    axios.post(`${API}/creative/sessions/${sessionId}/exploration/brief/review`,
               { decision: 'revise', feedback: (feedback || '').trim() }));

  // ── the lanes: one per KIND the service routes ────────────────────────────
  //
  // Built from the catalogue and the real target list, so a lane exists only if
  // the service can answer it, and a lane's queries are the actual stored
  // targets. Nothing here is a fixture.
  const reviewing = doc?.state === STATES.PROPOSED;
  // Memoised on purpose: a new array every render makes both `useMemo`s below
  // recompute every render, which the build flagged. The lanes and the readiness
  // are derived from this, so it has to be stable to be worth deriving.
  const liveTargets = useMemo(
    () => (reviewing
      ? draftTargets
      : (doc?.confirmed_targets || doc?.proposed_targets || [])),
    [reviewing, draftTargets, doc?.confirmed_targets, doc?.proposed_targets]);

  const lastRound = doc?.fetch_rounds?.length
    ? doc.fetch_rounds[doc.fetch_rounds.length - 1] : null;
  //: What the topical gate judged against, straight from the stored round.
  //: `subject_usable: false` means nothing was judged, which is NOT the same as
  //: everything having passed, and the screen says which.
  const relevance = lastRound?.relevance || null;

  const lanes = useMemo(() => kinds.map((k) => {
    const queries = liveTargets
      .map((t, i) => ({ ...t, index: i }))
      .filter((t) => (t.source_kind || '').toUpperCase() === k.kind);
    // WHAT THIS SOURCE DID ON THE LAST ROUND — and a FAILURE is not an empty
    // answer. EXPLORATION-OPTIONAL-SOURCES scope addition, 2026-09-11.
    //
    // Every result row already carries `ok` and `error`. This used to reduce all
    // of that to `items` plus `anyOk`, and the screen never read `anyOk` at all
    // — so a source that failed showed "returned nothing last run", identical to
    // one that succeeded and genuinely found nothing. Measured across 22 real
    // rounds and 128 source calls: 43 failed, 0 succeeded-and-returned-nothing.
    // Every "returned nothing" this screen ever showed was a failure.
    //
    // `anyOk` was also wrong on its own terms: `some(r => r.ok)` reports a
    // source whose calls DISAGREE inside one round as fully healthy. 5 real
    // cases of that in production. Calls are counted now, not sampled.
    const allForSource = (lastRound?.results || []).filter((r) => r.source === k.source);
    // A RETRY SUPERSEDES. Once the operator has re-run this source, the lane
    // reports what that attempt did — otherwise a retry that worked would read
    // as "1 of 2 calls failed", which is true of the round and false of the
    // source's current state. The original attempt is still in the round
    // summary, because what was tried is provenance.
    const retried = allForSource.filter((r) => r.operator_retry);
    const results = retried.length ? retried : allForSource;
    /* What the brief will actually see: distinct creatives that passed the
       topical gate. Counting rows here would make a lane read "5 results" for a
       round the brief was given one ad from — measured live, five rows for one
       query were five ad ids, one advertiser and one piece of copy. */
    const usable = (r) => (r.items || [])
      .filter((i) => !i.duplicate && !i.off_topic);
    const items = results.reduce((n, r) => n + usable(r).length, 0);
    // Rows collapsed as repeats of a creative already counted. Shown, not
    // hidden: an advertiser running one creative many times is spending on it.
    const repeats = results.reduce((n, r) => n + (r.items || [])
      .filter((i) => i.duplicate).length, 0);
    const okCalls = results.filter((r) => r.ok).length;
    const failedCalls = results.length - okCalls;
    // The reasons the source itself recorded, deduped and in order. Shown
    // verbatim — a fetcher's own words beat a sentence invented here.
    const errors = [...new Set(results.filter((r) => !r.ok)
      .map((r) => (r.error == null ? '' : String(r.error)).trim())
      .filter(Boolean))];
    return {
      ...k,
      selected: draftSources.length
        ? draftSources.includes(k.source)
        : (doc?.selected_sources || []).includes(k.source),
      queries,
      lastRun: results.length
        ? {
            ran: true,
            calls: results.length,
            okCalls,
            failedCalls,
            items,
            errors,
            // How many times the operator has re-run this source in this round.
            retries: retried.length,
            /* EXPLORATION-RELEVANCE-GATE. Items the topical gate judged to be
               about a DIFFERENT subject. They are still in the record — nothing
               is deleted — but they are kept out of the brief, so the operator
               has to be able to see that happened and on what basis. An
               operator who cannot see the exclusions cannot tell a working gate
               from a broken one. */
            offTopic: results.reduce((n, r) => n + (r.items || [])
              .filter((i) => !i.duplicate && i.off_topic).length, 0),
            onTopic: items,
            // Repeats of a creative already counted, and the largest number of
            // times any single creative was run in this round.
            repeats,
            maxVariants: results.reduce((m, r) => Math.max(m,
              ...usable(r).map((i) => Number(i.variants) || 1), 0), 0),
            // FOUR outcomes, named once here so no screen has to re-derive
            // them and get it wrong:
            //   'failed'    every call errored — nothing came back and it is a
            //               fault, not an answer
            //   'partial'   some calls worked, some errored — the source is
            //               half-reported and must say so
            //   'empty'     it ran, it worked, and there was genuinely nothing
            //   'ok'        it ran and returned results
            outcome: failedCalls === results.length ? 'failed'
                   : failedCalls > 0 ? 'partial'
                   : items === 0 ? 'empty'
                   : 'ok',
          }
        : { ran: false },
      // A PAGE_SCRAPE label that is not a URL cannot be fetched. Real and
      // checkable, so it is a named blocker rather than a surprise failure.
      badUrls: k.kind === 'PAGE_SCRAPE'
        ? queries.filter((q) => (q.label || '').trim() && !looksLikeUrl(q.label)).length
        : 0,
    };
  }), [kinds, liveTargets, draftSources, doc?.selected_sources, lastRound]);

  // ── readiness: every number traceable to a field ──────────────────────────
  const readiness = useMemo(() => {
    const labelled = liveTargets.filter((t) => (t.label || '').trim()).length;
    const selectedSources = lanes.filter((l) => l.selected).map((l) => l.source);
    const uniqueSelected = [...new Set(selectedSources)];
    // BLOCKERS STOP THE RUN. WARNINGS COST SOMETHING AND LET IT PROCEED.
    //
    // EXPLORATION-OPTIONAL-SOURCES, 2026-09-11. These were one list, so an
    // included source with no query stopped the whole plan — the Founder could
    // not confirm 6 queries across 4 working sources because a 5th, optional,
    // sat empty. Founder's ruling: only a source that is both included AND
    // required may block; an empty optional source runs as-is and contributes
    // nothing.
    //
    // `required` is a declared flag on the source catalogue
    // (`exploration_service.KIND_CATALOGUE`), carried onto the lane by the
    // `...k` spread above. It is `false` on all five kinds today, so no source
    // currently blocks by being empty — but the gate reads the flag rather than
    // assuming, so marking one required re-engages it by name.
    //
    // The comment on `ready` below has always said readiness means only "a
    // query exists and somewhere to send it". Now the code agrees with it.
    const blockers = [];
    const warnings = [];

    if (labelled === 0) {
      blockers.push('No research query has been written yet, so there is '
                  + 'nothing to look up.');
    }
    if (uniqueSelected.length === 0) {
      blockers.push('No source is selected, so no query can be sent anywhere.');
    }
    lanes.filter((l) => l.selected).forEach((l) => {
      if (l.queries.filter((q) => (q.label || '').trim()).length === 0) {
        const fallback = (catalogue?.fallback_eligible || []).includes(l.source);
        // A REQUIRED source cannot sit empty. Named, so it is never a quiet
        // pass — this is the half of the ruling that must not be lost.
        if (l.required) {
          blockers.push(`${l.label} is a required source and has no query. `
                      + `${fallback
                          ? 'It would fall back to the offer name, which is not '
                            + 'a research question.'
                          : 'It cannot be given one automatically.'}`);
          return;
        }
        warnings.push(fallback
          ? `${l.label} has no query of its own, so it will fall back to the `
            + `offer name. The run will proceed.`
          : `${l.label} has no query and cannot be given one automatically, so `
            + `it will contribute nothing. The run will proceed without it.`);
      }
      if (l.badUrls) {
        // NOT relaxed with the rest. A label that is not a URL is a MALFORMED
        // entry the operator can fix in seconds, not an absence — and spending
        // a fetch round on it is the waste this check exists to prevent.
        blockers.push(`${l.label}: ${l.badUrls} entr${l.badUrls === 1 ? 'y is' : 'ies are'} `
                    + `not a URL, so ${l.badUrls === 1 ? 'it' : 'they'} cannot be read.`);
      }
    });

    const budget = doc?.budget || {};
    const used = budget.fetch_rounds_used;
    const max = budget.max_fetch_rounds;
    return {
      // "Ready" here means only: a query exists and somewhere to send it. It is
      // not a quality score, and it does not claim the research will be good.
      ready: blockers.length === 0,
      labelledQueries: labelled,
      totalQueries: liveTargets.length,
      sourcesSelected: uniqueSelected.length,
      sourcesRegistered: registered.length,
      blockers,
      // Costs, not faults. Kept separate so a screen cannot render one as the
      // other, and so `ready` can never be derived from them by accident.
      warnings,
      // From `budget`, or null when the document has none. Never assumed.
      roundsUsed: Number.isFinite(used) ? used : null,
      roundsMax: Number.isFinite(max) ? max : null,
      itemsPerSource: Number.isFinite(budget.items_per_source)
        ? budget.items_per_source : null,
    };
  }, [lanes, liveTargets, doc?.budget, registered.length, catalogue]);

  // ── where each target came from, from the stored field ───────────────────
  const origins = useMemo(() => {
    const rows = doc?.confirmed_targets || [];
    const count = (v) => rows.filter((t) => t.origin === v).length;
    return {
      recorded: rows.some((t) => t.origin),
      suggested: count('SUGGESTED'),
      edited: count('EDITED'),
      added: count('ADDED'),
      unknown: count('UNKNOWN'),
    };
  }, [doc?.confirmed_targets]);

  return {
    doc, state: doc?.state || null,
    loadState, loadError, busy, actionError, setActionError,
    catalogue, kinds, lanes, readiness, origins, lastRound,
    reviewing, draftTargets, draftSources,
    setTarget, removeTarget, addTarget, toggleSource,
    saveObjective, confirmTargets, runFetch, retrySource, sendMessage,
    relevance,
    synthesise, approveBrief, reviseBrief,
  };
}
